import { ComponentType } from '@/types';

export type CardCategory = 'gauge' | 'pin' | 'simple';

export interface ActionButtonConfig {
  label: string;
  /** 'autoSend' fires the command immediately, 'preFill' sets input, 'drilldown' opens sub-panel, 'responsiveColumns' opens table viewport column controls, 'dataAdditionsToggle' toggles table data-add controls, 'dataDiscovery' triggers data discovery, 'removeData' opens remove-data confirmation, 'iconPicker' opens icon picker modal, 'mapTemplates' opens map templates browser, 'colorPicker' opens color picker modal, 'deviceBinding' opens the unified device provider flow, 'homeAssistantBinding' opens the safe HA entity binder, 'homeAssistantUnbind' removes HA metadata, 'homeAssistantExplain' explains stored HA metadata, 'duplicateComponent'/'hideComponent' use safe local editor actions */
  action: 'autoSend' | 'preFill' | 'drilldown' | 'responsiveColumns' | 'dataAdditionsToggle' | 'dataDiscovery' | 'removeData' | 'iconPicker' | 'mapTemplates' | 'colorPicker' | 'deviceBinding' | 'homeAssistantBinding' | 'homeAssistantUnbind' | 'homeAssistantExplain' | 'duplicateComponent' | 'hideComponent';
  command?: string;
  drilldownId?: string;
}

export interface ActionCardConfig {
  icon: string;
  marker: string;
  titleFn: (props: Record<string, unknown>) => string;
  summaryFn?: (props: Record<string, unknown>) => string | null;
  category: CardCategory;
  buttons: ActionButtonConfig[];
}

const simpleCard = (
  icon: string,
  marker: string,
  titleFn: (props: Record<string, unknown>) => string,
  buttons: ActionButtonConfig[],
  summaryFn?: (props: Record<string, unknown>) => string | null,
): ActionCardConfig => ({
  icon,
  marker,
  titleFn,
  summaryFn,
  category: 'simple',
  buttons,
});

const getString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const getName = (props: Record<string, unknown>, fallback: string) =>
  getString(props.title) || getString(props.name) || getString(props.label) || fallback;

export const actionCardRegistry: Record<string, ActionCardConfig> = {
  [ComponentType.GAUGE_CHART]: {
    icon: '◎',
    marker: '◎ Gauge selected:',
    titleFn: (props) => getName(props, 'Gauge'),
    category: 'gauge',
    buttons: [],
  },
  [ComponentType.VERTICAL_GAUGE_CHART]: {
    icon: '◎',
    marker: '◎ Gauge selected:',
    titleFn: (props) => getName(props, 'Gauge'),
    category: 'gauge',
    buttons: [],
  },
  [ComponentType.MAP_CHART]: simpleCard('◈', '◈ Map selected:', (props) => getName(props, 'Map'), [
    { label: 'Place Pin', action: 'autoSend', command: 'place pin on map' },
    { label: 'View', action: 'drilldown', drilldownId: 'mapView' },
    { label: 'Layers', action: 'drilldown', drilldownId: 'mapLayers' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'mapTheme' },
    { label: 'Groups', action: 'preFill', command: 'create group ' },
    { label: 'Display', action: 'drilldown', drilldownId: 'mapDisplay' },
    { label: 'Lock Location', action: 'autoSend', command: 'lock location' },
    { label: 'Templates', action: 'mapTemplates' },
  ]),
  [ComponentType.TEXT]: simpleCard('¶', '¶ Text selected:', (props) => getName(props, 'Text'), [
    { label: 'Edit Text', action: 'preFill', command: 'set text to ' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Font Size', action: 'drilldown', drilldownId: 'fontSize' },
    { label: 'Font Weight', action: 'drilldown', drilldownId: 'fontWeight' },
    { label: 'Heading Level', action: 'drilldown', drilldownId: 'headingLevel' },
    { label: 'Align', action: 'drilldown', drilldownId: 'align' },
    { label: 'Color', action: 'colorPicker' },
  ]),
  [ComponentType.DATA_TEXT]: simpleCard('⊞', '⊞ Data Text selected:', (props) => getName(props, 'Data Text'), [
    { label: 'Edit Label', action: 'preFill', command: 'set label to ' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Format', action: 'drilldown', drilldownId: 'dataTextFormat' },
    { label: 'Font Size', action: 'drilldown', drilldownId: 'fontSize' },
    { label: 'Font Weight', action: 'drilldown', drilldownId: 'fontWeight' },
    { label: 'Align', action: 'drilldown', drilldownId: 'align' },
    { label: 'Color', action: 'colorPicker' },
  ]),
  [ComponentType.BUTTON]: simpleCard('▣', '▣ Button selected:', (props) => getName(props, 'Button'), [
    { label: 'Edit Label', action: 'preFill', command: 'set label to ' },
    { label: 'Change Icon', action: 'iconPicker' },
    { label: 'Variant', action: 'drilldown', drilldownId: 'buttonVariant' },
    { label: 'Size', action: 'drilldown', drilldownId: 'buttonSize' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Disabled', action: 'autoSend', command: 'toggle disabled' },
  ]),
  [ComponentType.RADIAL_DIAL]: simpleCard('◉', '◉ Dial selected:', (props) => getName(props, 'Dial'), [
    { label: 'Range', action: 'preFill', command: 'set range ' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'dialTheme' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Device', action: 'deviceBinding' },
  ]),
  [ComponentType.TOGGLE_SWITCH]: simpleCard('⏻', '⏻ Toggle Switch selected:', (props) => getName(props, 'Switch'), [
    { label: 'Theme', action: 'drilldown', drilldownId: 'toggleTheme' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Device', action: 'deviceBinding' },
  ]),
  [ComponentType.STATUS_LIGHT]: simpleCard('●', '● Status Light selected:', (props) => getName(props, 'Status Light'), [
    { label: 'Thresholds', action: 'drilldown', drilldownId: 'statusLightThresholds' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'statusLightTheme' },
    { label: 'Shape', action: 'drilldown', drilldownId: 'statusLightShape' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Blink', action: 'drilldown', drilldownId: 'statusLightBlink' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
  ]),
  [ComponentType.PUSH_BUTTON]: simpleCard('⏺', '⏺ Push Button selected:', (props) => getName(props, 'Push Button'), [
    { label: 'Theme', action: 'drilldown', drilldownId: 'pushButtonTheme' },
    { label: 'Shape', action: 'drilldown', drilldownId: 'pushButtonShape' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Label', action: 'preFill', command: 'set label to ' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Device', action: 'deviceBinding' },
  ]),
  [ComponentType.ICON_BUTTON]: simpleCard('⊡', '⊡ Icon Button selected:', (props) => getName(props, 'Icon Button'), [
    { label: 'Change Icon', action: 'iconPicker' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'iconButtonTheme' },
    { label: 'Shape', action: 'drilldown', drilldownId: 'iconButtonShape' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Toggle', action: 'autoSend', command: 'toggle' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Device', action: 'deviceBinding' },
  ]),
  [ComponentType.STEPPER]: simpleCard('±', '± Stepper selected:', (props) => getName(props, 'Stepper'), [
    { label: 'Range', action: 'preFill', command: 'set range ' },
    { label: 'Step', action: 'preFill', command: 'set step to ' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'stepperTheme' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
  ]),
  [ComponentType.SLIDER]: simpleCard('━', '━ Slider selected:', (props) => getName(props, 'Slider'), [
    { label: 'Range', action: 'preFill', command: 'set range ' },
    { label: 'Step', action: 'preFill', command: 'set step to ' },
    { label: 'Gauge', action: 'autoSend', command: 'make this selected component a vertical gauge slider' },
    { label: 'Ticks', action: 'drilldown', drilldownId: 'sliderTicks' },
    { label: 'Tick Interval', action: 'preFill', command: 'set tick interval to ' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'sliderTheme' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Device', action: 'deviceBinding' },
  ]),
  [ComponentType.LEVEL_INDICATOR]: simpleCard('▮', '▮ Level Indicator selected:', (props) => getName(props, 'Level'), [
    { label: 'Segments', action: 'preFill', command: 'set segments to ' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'levelTheme' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Orientation', action: 'drilldown', drilldownId: 'levelOrientation' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
  ]),
  [ComponentType.SEGMENTED_CONTROL]: simpleCard('▦', '▦ Segmented selected:', (props) => getName(props, 'Segmented Control'), [
    { label: 'Options', action: 'preFill', command: 'set options ' },
    { label: 'Theme', action: 'drilldown', drilldownId: 'segmentedTheme' },
    { label: 'Select', action: 'preFill', command: 'select ' },
  ]),
  [ComponentType.COLOR_PICKER]: simpleCard('🎨', '🎨 Color Picker:', (props) => getName(props, 'Color Picker'), [
    { label: 'Color', action: 'preFill', command: 'set color to ' },
    { label: 'Mode', action: 'preFill', command: 'mode ' },
    { label: 'Theme', action: 'preFill', command: 'theme ' },
  ]),
  [ComponentType.THERMOSTAT]: simpleCard('🌡', '🌡 Thermostat:', (props) => getName(props, 'Thermostat'), [
    { label: 'Target', action: 'preFill', command: 'set target to ' },
    { label: 'Mode', action: 'preFill', command: 'mode ' },
    { label: 'Units', action: 'drilldown', drilldownId: 'thermostatUnits' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Theme', action: 'preFill', command: 'theme ' },
  ]),
  [ComponentType.IMAGE]: simpleCard('⬡', '⬡ Image selected:', (props) => getName(props, 'Image'), [
    { label: 'Change Source', action: 'preFill', command: 'set image url to ' },
    { label: 'Alt Text', action: 'preFill', command: 'set alt text to ' },
    { label: 'Object Fit', action: 'drilldown', drilldownId: 'objectFit' },
    { label: 'Border Radius', action: 'drilldown', drilldownId: 'borderRadius' },
  ]),
  [ComponentType.INPUT]: simpleCard('⌸', '⌸ Input selected:', (props) => getName(props, 'Input'), [
    { label: 'Placeholder', action: 'preFill', command: 'set placeholder to ' },
    { label: 'Label', action: 'preFill', command: 'set label to ' },
    { label: 'Helper Text', action: 'preFill', command: 'set helper text to ' },
    { label: 'Type', action: 'drilldown', drilldownId: 'inputType' },
    { label: 'Required', action: 'autoSend', command: 'toggle required' },
    { label: 'Validation', action: 'drilldown', drilldownId: 'inputValidation' },
    { label: 'Style', action: 'drilldown', drilldownId: 'inputStyle' },
    { label: 'Color', action: 'colorPicker' },
  ]),
  [ComponentType.CONTAINER]: simpleCard('⬚', '⬚ Container selected:', (props) => getName(props, 'Container'), [
    { label: 'Direction', action: 'drilldown', drilldownId: 'flexDirection' },
    { label: 'Align', action: 'drilldown', drilldownId: 'flexAlign' },
    { label: 'Justify', action: 'drilldown', drilldownId: 'flexJustify' },
    { label: 'Gap', action: 'drilldown', drilldownId: 'gap' },
    { label: 'Padding', action: 'drilldown', drilldownId: 'padding' },
    { label: 'Background', action: 'drilldown', drilldownId: 'background' },
  ]),
  [ComponentType.FLEX_CONTAINER]: simpleCard('⬚', '⬚ Flex Container selected:', (props) => getName(props, 'Flex Container'), [
    { label: 'Direction', action: 'drilldown', drilldownId: 'flexDirection' },
    { label: 'Align', action: 'drilldown', drilldownId: 'flexAlign' },
    { label: 'Justify', action: 'drilldown', drilldownId: 'flexJustify' },
    { label: 'Gap', action: 'drilldown', drilldownId: 'gap' },
    { label: 'Padding', action: 'drilldown', drilldownId: 'padding' },
    { label: 'Background', action: 'drilldown', drilldownId: 'background' },
  ]),
  [ComponentType.CARD]: simpleCard('▢', '▢ Card selected:', (props) => getName(props, 'Card'), [
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Padding', action: 'drilldown', drilldownId: 'padding' },
    { label: 'Background', action: 'drilldown', drilldownId: 'background' },
    { label: 'Color', action: 'colorPicker' },
    { label: 'Shadow', action: 'drilldown', drilldownId: 'shadow' },
  ]),
  [ComponentType.ICON]: simpleCard('✦', '✦ Icon selected:', () => 'Icon', [
    { label: 'Change Icon', action: 'iconPicker' },
    { label: 'Size', action: 'drilldown', drilldownId: 'iconSize' },
    { label: 'Color', action: 'colorPicker' },
  ]),
  [ComponentType.BAR_CHART]: simpleCard('▥', '▥ Bar Chart selected:', (props) => getName(props, 'Bar Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Chart Type', action: 'drilldown', drilldownId: 'chartType' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
    { label: 'Grid', action: 'autoSend', command: 'toggle grid' },
  ]),
  [ComponentType.LINE_CHART]: simpleCard('⟋', '⟋ Line Chart selected:', (props) => getName(props, 'Line Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Chart Type', action: 'drilldown', drilldownId: 'chartType' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
    { label: 'Grid', action: 'autoSend', command: 'toggle grid' },
  ]),
  [ComponentType.PIE_CHART]: simpleCard('◔', '◔ Pie Chart selected:', (props) => getName(props, 'Pie Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
    { label: 'Tooltip', action: 'autoSend', command: 'toggle tooltip' },
  ]),
  [ComponentType.SCATTER_CHART]: simpleCard('⁘', '⁘ Scatter Chart selected:', (props) => getName(props, 'Scatter Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
    { label: 'Grid', action: 'autoSend', command: 'toggle grid' },
  ]),
  [ComponentType.AREA_CHART]: simpleCard('△', '△ Area Chart selected:', (props) => getName(props, 'Area Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Chart Type', action: 'drilldown', drilldownId: 'chartType' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
    { label: 'Grid', action: 'autoSend', command: 'toggle grid' },
  ]),
  [ComponentType.HEATMAP_CHART]: simpleCard('▦', '▦ Heatmap selected:', (props) => getName(props, 'Heatmap'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Cell Labels', action: 'autoSend', command: 'enable cell labels' },
    { label: 'Row Totals', action: 'autoSend', command: 'show row totals' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
  ]),
  [ComponentType.TREEMAP_CHART]: simpleCard('⊞', '⊞ Treemap selected:', (props) => getName(props, 'Treemap'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
  ]),
  [ComponentType.CHART]: simpleCard('▥', '▥ Chart selected:', (props) => getName(props, 'Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Chart Type', action: 'drilldown', drilldownId: 'chartType' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Legend', action: 'autoSend', command: 'toggle legend' },
    { label: 'Grid', action: 'autoSend', command: 'toggle grid' },
    { label: 'Color', action: 'colorPicker' },
  ]),
  [ComponentType.TABLE]: simpleCard('▤', '▤ Table selected:', (props) => getName(props, 'Table'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Add Column', action: 'preFill', command: 'add column ' },
    { label: 'Enable Data Add', action: 'dataAdditionsToggle' },
    { label: 'Columns', action: 'responsiveColumns' },
    { label: 'Sorting', action: 'autoSend', command: 'toggle sorting' },
    { label: 'Striped', action: 'autoSend', command: 'toggle striped rows' },
  ]),
  [ComponentType.MODAL]: simpleCard('⬜', '⬜ Modal selected:', (props) => getName(props, 'Modal'), [
    { label: 'Title', action: 'preFill', command: 'title ' },
    { label: 'Size', action: 'preFill', command: 'size ' },
    { label: 'Trigger', action: 'preFill', command: 'trigger text ' },
    { label: 'Backdrop', action: 'autoSend', command: 'toggle backdrop' },
  ]),
  [ComponentType.ADVANCED_TABLE]: simpleCard('▤', '▤ Advanced Table selected:', (props) => getName(props, 'Advanced Table'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Add Column', action: 'preFill', command: 'add column ' },
    { label: 'Enable Data Add', action: 'dataAdditionsToggle' },
    { label: 'Columns', action: 'responsiveColumns' },
    { label: 'Filtering', action: 'autoSend', command: 'toggle filtering' },
    { label: 'Sorting', action: 'autoSend', command: 'toggle sorting' },
  ]),
  [ComponentType.DATA_CARD]: simpleCard('◧', '◧ Data Card selected:', (props) => getName(props, 'Data Card'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Format', action: 'drilldown', drilldownId: 'dataCardFormat' },
    { label: 'Change Icon', action: 'iconPicker' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Layout', action: 'drilldown', drilldownId: 'flexDirection' },
  ]),
  [ComponentType.FERMENTATION_TANK_CARD]: simpleCard('⌬', '⌬ Tank Card selected:', (props) => getName(props, 'Tank Card'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Title', action: 'preFill', command: 'set title to ' },
  ]),
  [ComponentType.LIST]: simpleCard('≡', '≡ List selected:', (props) => getName(props, 'List'), [
    { label: 'Add Item', action: 'preFill', command: 'add item ' },
    { label: 'Style', action: 'drilldown', drilldownId: 'listStyle' },
  ]),
  [ComponentType.CUSTOM]: simpleCard('✧', '✧ Custom selected:', (props) => getName(props, 'Custom'), [
    { label: 'Edit', action: 'preFill', command: 'modify ' },
  ]),
  [ComponentType.FORM_CONTAINER]: simpleCard('☰', '☰ Form selected:', (props) => getName(props, 'Form'), [
    { label: 'Add Field', action: 'preFill', command: 'add field ' },
    { label: 'Layout', action: 'drilldown', drilldownId: 'flexDirection' },
    { label: 'Gap', action: 'drilldown', drilldownId: 'gap' },
  ]),
  [ComponentType.BREADCRUMB]: simpleCard('⟩', '⟩ Breadcrumb selected:', (props) => getName(props, 'Breadcrumb'), [
    { label: 'Separator', action: 'drilldown', drilldownId: 'separator' },
    { label: 'Color', action: 'colorPicker' },
  ]),
  [ComponentType.FILE_STORAGE]: simpleCard('⊟', '⊟ File Storage selected:', (props) => getName(props, 'File Storage'), [
    { label: 'Title', action: 'preFill', command: 'set title to ' },
    { label: 'Grid View', action: 'autoSend', command: 'switch to grid view' },
    { label: 'List View', action: 'autoSend', command: 'switch to list view' },
    { label: 'Uploads', action: 'autoSend', command: 'enable uploads' },
    { label: 'Delete Enabled', action: 'autoSend', command: 'toggle delete enabled' },
    { label: 'Search', action: 'autoSend', command: 'toggle search' },
    { label: 'Size', action: 'drilldown', drilldownId: 'selectedSize' },
  ]),
  [ComponentType.KANBAN_BOARD]: simpleCard('⫿', '⫿ Kanban Board selected:', (props) => getName(props, 'Kanban Board'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Add Column', action: 'preFill', command: 'add column ' },
    { label: 'WIP Limit', action: 'preFill', command: 'set wip limit ' },
    { label: 'Card Layout', action: 'preFill', command: 'set card layout ' },
  ]),
  [ComponentType.CHECKLIST]: simpleCard('☑️', '☑️ Checklist selected:', (props) => getName(props, 'Checklist'), [
    { label: 'Add Item', action: 'preFill', command: 'add item ' },
    { label: 'Toggle All', action: 'autoSend', command: 'check all items' },
    { label: 'Group By', action: 'preFill', command: 'group by ' },
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
  ]),
  [ComponentType.HORIZONTAL_TIMELINE]: simpleCard('📅', '📅 Timeline selected:', (props) => getName(props, 'Timeline'), [
    { label: 'Add Event', action: 'preFill', command: 'add event ' },
    { label: 'Gantt View', action: 'autoSend', command: 'set variant gantt' },
    { label: 'Milestone View', action: 'autoSend', command: 'set variant milestone' },
    { label: 'Toggle Dates', action: 'autoSend', command: 'toggle dates' },
  ]),
  [ComponentType.GANTT_CHART]: simpleCard('📊', '📊 Gantt Chart selected:', (props) => getName(props, 'Gantt Chart'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Time Scale', action: 'preFill', command: 'set time scale ' },
    { label: 'Group By', action: 'preFill', command: 'group by ' },
    { label: 'Dependencies', action: 'preFill', command: 'show dependencies' },
  ]),
  [ComponentType.CALENDAR]: simpleCard('📅', '📅 Calendar selected:', (props) => getName(props, 'Calendar'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Month View', action: 'autoSend', command: 'switch to month view' },
    { label: 'Week View', action: 'autoSend', command: 'switch to week view' },
    { label: 'Business Hours', action: 'preFill', command: 'set business hours ' },
  ]),
  [ComponentType.ASSIGNMENT_WIDGET]: simpleCard('👥', '👥 Assignments selected:', (props) => getName(props, 'Assignments'), [
    { label: 'Add Person', action: 'preFill', command: 'add person ' },
    { label: 'List Layout', action: 'autoSend', command: 'set layout list' },
    { label: 'Grid Layout', action: 'autoSend', command: 'set layout grid' },
    { label: 'Compact', action: 'autoSend', command: 'set layout compact' },
  ]),
  [ComponentType.PROJECT_HUB]: simpleCard('🗂️', '🗂️ Project Hub selected:', (props) => getName(props, 'Project Hub'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Table View', action: 'autoSend', command: 'switch to table view' },
    { label: 'Board View', action: 'autoSend', command: 'switch to board view' },
    { label: 'Timeline View', action: 'autoSend', command: 'switch to timeline view' },
  ]),
  [ComponentType.QUESTIONNAIRE]: simpleCard('📋', '📋 Questionnaire selected:', (props) => getName(props, 'Questionnaire'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Remove Data', action: 'removeData' },
    { label: 'Add Step', action: 'preFill', command: 'add step ' },
    { label: 'Show Results', action: 'autoSend', command: 'show results' },
    { label: 'Show Form', action: 'autoSend', command: 'show form' },
    { label: 'Card Layout', action: 'autoSend', command: 'set layout card' },
    { label: 'Stacked Layout', action: 'autoSend', command: 'set layout stacked' },
  ]),
  [ComponentType.VOTING_CARD]: simpleCard('\u{1F5F3}\u{FE0F}', '\u{1F5F3}\u{FE0F} Voting Card selected:', (props) => getName(props, 'Vote'), [
    { label: 'Add Data', action: 'dataDiscovery' },
    { label: 'Add Option', action: 'preFill', command: 'add option ' },
    { label: 'Approve/Veto', action: 'autoSend', command: 'set vote type to approve-veto' },
    { label: 'Single Choice', action: 'autoSend', command: 'set vote type to single-choice' },
    { label: 'Ranked', action: 'autoSend', command: 'set vote type to ranked' },
    { label: 'Grid Layout', action: 'autoSend', command: 'set layout grid' },
  ]),
};

export function getActionCardConfig(componentType: string): ActionCardConfig | undefined {
  return actionCardRegistry[componentType];
}

/** Drilldown option sets for simple action cards */
export interface DrilldownOption { label: string; command: string }
export interface DrilldownConfig { options: DrilldownOption[]; customPrefix?: string }
export const simpleDrilldowns: Record<string, DrilldownConfig> = {
  selectedSpacing: { options: [
    { label: 'Compact', command: 'make this selected component more compact' },
    { label: 'Roomy', command: 'make this selected component roomier' },
  ]},
  selectedSize: { options: [
    { label: 'Smaller', command: 'make this selected component smaller' },
    { label: 'Larger', command: 'make this selected component larger' },
  ]},
  selectedAlign: { options: [
    { label: 'Left', command: 'align this selected component left' },
    { label: 'Center', command: 'align this selected component center' },
    { label: 'Right', command: 'align this selected component right' },
  ]},
  selectedHomeAssistantAdd: { options: [
    { label: 'Status Card', command: 'add a read-only Home Assistant entity status card inside this selected component' },
    { label: 'Light Control', command: 'add an inert Home Assistant light control block inside this selected component' },
    { label: 'Thermostat', command: 'add an inert Home Assistant thermostat block inside this selected component' },
    { label: 'Energy Metric', command: 'add a read-only Home Assistant energy metric block inside this selected component' },
    { label: 'Security Sensor', command: 'add a read-only Home Assistant security sensor block inside this selected component' },
    { label: 'Device Health', command: 'add a read-only Home Assistant device health block inside this selected component' },
    { label: 'Irrigation Status', command: 'add a read-only Home Assistant irrigation status block inside this selected component' },
  ]},
  fontSize: { customPrefix: 'set font size to ', options: [
    { label: '12px', command: 'set font size to 12' },
    { label: '14px', command: 'set font size to 14' },
    { label: '16px', command: 'set font size to 16' },
    { label: '20px', command: 'set font size to 20' },
    { label: '24px', command: 'set font size to 24' },
    { label: '32px', command: 'set font size to 32' },
  ]},
  fontWeight: { options: [
    { label: 'Normal', command: 'set font weight to normal' },
    { label: 'Medium', command: 'set font weight to 500' },
    { label: 'Semibold', command: 'set font weight to 600' },
    { label: 'Bold', command: 'set font weight to bold' },
  ]},
  align: { options: [
    { label: 'Left', command: 'align text left' },
    { label: 'Center', command: 'align text center' },
    { label: 'Right', command: 'align text right' },
  ]},
  dataTextFormat: { options: [
    { label: 'Number', command: 'format as number' },
    { label: 'Compact', command: 'format as compact' },
    { label: 'Currency', command: 'format as currency' },
    { label: 'Percent', command: 'format as percent' },
    { label: 'Integer', command: 'format as integer' },
    { label: 'Date', command: 'format as date' },
    { label: 'Time', command: 'format as time' },
    { label: 'DateTime', command: 'format as datetime' },
    { label: '0 Decimals', command: 'set decimal places to 0' },
    { label: '1 Decimal', command: 'set decimal places to 1' },
    { label: '2 Decimals', command: 'set decimal places to 2' },
    { label: 'Thousands', command: 'add thousands separator' },
    { label: 'No Thousands', command: 'remove thousands separator' },
    { label: 'Remove Unit', command: 'remove unit' },
  ]},
  dataCardFormat: { options: [
    { label: 'Number', command: 'format as number' },
    { label: 'Compact', command: 'format as compact' },
    { label: 'Currency', command: 'format as currency' },
    { label: 'Percent', command: 'format as percent' },
    { label: 'Integer', command: 'format as integer' },
    { label: 'Date', command: 'format as date' },
    { label: 'Time', command: 'format as time' },
    { label: 'DateTime', command: 'format as datetime' },
    { label: '0 Decimals', command: 'set decimal places to 0' },
    { label: '1 Decimal', command: 'set decimal places to 1' },
    { label: '2 Decimals', command: 'set decimal places to 2' },
    { label: 'Thousands', command: 'add thousands separator' },
    { label: 'No Thousands', command: 'remove thousands separator' },
  ]},
  color: { customPrefix: 'set color to ', options: [
    { label: 'Black', command: 'set color to black' },
    { label: 'White', command: 'set color to white' },
    { label: 'Gray', command: 'set color to gray' },
    { label: 'Red', command: 'set color to red' },
    { label: 'Blue', command: 'set color to blue' },
    { label: 'Green', command: 'set color to green' },
  ]},
  buttonVariant: { options: [
    { label: 'Default', command: 'set variant to default' },
    { label: 'Outline', command: 'set variant to outline' },
    { label: 'Ghost', command: 'set variant to ghost' },
    { label: 'Destructive', command: 'set variant to destructive' },
  ]},
  buttonSize: { options: [
    { label: 'Small', command: 'set size to small' },
    { label: 'Default', command: 'set size to default' },
    { label: 'Large', command: 'set size to large' },
  ]},
  objectFit: { options: [
    { label: 'Cover', command: 'set object fit to cover' },
    { label: 'Contain', command: 'set object fit to contain' },
    { label: 'Fill', command: 'set object fit to fill' },
    { label: 'None', command: 'set object fit to none' },
  ]},
  borderRadius: { customPrefix: 'set border radius to ', options: [
    { label: '0', command: 'set border radius to 0' },
    { label: '4px', command: 'set border radius to 4' },
    { label: '8px', command: 'set border radius to 8' },
    { label: '16px', command: 'set border radius to 16' },
    { label: 'Full', command: 'set border radius to 9999' },
  ]},
  inputType: { options: [
    { label: 'Text', command: 'set input type to text' },
    { label: 'Email', command: 'set input type to email' },
    { label: 'Password', command: 'set input type to password' },
    { label: 'Number', command: 'set input type to number' },
  ]},
  inputValidation: { options: [
    { label: 'Email', command: 'set validation pattern to email' },
    { label: 'Phone', command: 'set validation pattern to phone' },
    { label: 'URL', command: 'set validation pattern to url' },
    { label: 'Numbers Only', command: 'set validation pattern to numbers-only' },
    { label: 'Letters Only', command: 'set validation pattern to letters-only' },
  ]},
  inputStyle: { options: [
    { label: 'Outline', command: 'set variant to outline' },
    { label: 'Filled', command: 'set variant to filled' },
    { label: 'Underline', command: 'set variant to underline' },
    { label: 'Ghost', command: 'set variant to ghost' },
  ]},
  flexDirection: { options: [
    { label: 'Row', command: 'set direction to row' },
    { label: 'Column', command: 'set direction to column' },
    { label: 'Row Reverse', command: 'set direction to row-reverse' },
    { label: 'Column Reverse', command: 'set direction to column-reverse' },
  ]},
  flexAlign: { options: [
    { label: 'Start', command: 'set align items to flex-start' },
    { label: 'Center', command: 'set align items to center' },
    { label: 'End', command: 'set align items to flex-end' },
    { label: 'Stretch', command: 'set align items to stretch' },
  ]},
  flexJustify: { options: [
    { label: 'Start', command: 'set justify content to flex-start' },
    { label: 'Center', command: 'set justify content to center' },
    { label: 'End', command: 'set justify content to flex-end' },
    { label: 'Between', command: 'set justify content to space-between' },
    { label: 'Around', command: 'set justify content to space-around' },
  ]},
  gap: { customPrefix: 'set gap to ', options: [
    { label: '0', command: 'set gap to 0' },
    { label: '4px', command: 'set gap to 4' },
    { label: '8px', command: 'set gap to 8' },
    { label: '12px', command: 'set gap to 12' },
    { label: '16px', command: 'set gap to 16' },
    { label: '24px', command: 'set gap to 24' },
  ]},
  padding: { customPrefix: 'set padding to ', options: [
    { label: '0', command: 'set padding to 0' },
    { label: '4px', command: 'set padding to 4' },
    { label: '8px', command: 'set padding to 8' },
    { label: '12px', command: 'set padding to 12' },
    { label: '16px', command: 'set padding to 16' },
    { label: '24px', command: 'set padding to 24' },
  ]},
  background: { customPrefix: 'set background to ', options: [
    { label: 'White', command: 'set background to white' },
    { label: 'Light Gray', command: 'set background to #F3F4F6' },
    { label: 'Blue Tint', command: 'set background to #EFF6FF' },
    { label: 'Surface', command: 'set background to #F9FAFB' },
    { label: 'Transparent', command: 'set background to transparent' },
    { label: 'Dark', command: 'set background to #1F2937' },
  ]},
  shadow: { options: [
    { label: 'None', command: 'remove shadow' },
    { label: 'Small', command: 'set shadow to small' },
    { label: 'Medium', command: 'set shadow to medium' },
    { label: 'Large', command: 'set shadow to large' },
  ]},
  iconSize: { customPrefix: 'set icon size to ', options: [
    { label: '16px', command: 'set icon size to 16' },
    { label: '20px', command: 'set icon size to 20' },
    { label: '24px', command: 'set icon size to 24' },
    { label: '32px', command: 'set icon size to 32' },
  ]},
  chartType: { options: [
    { label: 'Bar', command: 'change to bar chart' },
    { label: 'Line', command: 'change to line chart' },
    { label: 'Area', command: 'change to area chart' },
    { label: 'Pie', command: 'change to pie chart' },
  ]},
  listStyle: { options: [
    { label: 'Bullet', command: 'set list style to bullet' },
    { label: 'Numbered', command: 'set list style to numbered' },
    { label: 'None', command: 'set list style to none' },
  ]},
  separator: { customPrefix: 'set separator to ', options: [
    { label: '/', command: 'set separator to /' },
    { label: '>', command: 'set separator to >' },
    { label: '-', command: 'set separator to -' },
  ]},
  mapView: { options: [
    { label: 'Satellite', command: 'satellite view' },
    { label: 'Streets', command: 'streets view' },
    { label: 'Hybrid', command: 'hybrid view' },
  ]},
  mapLayers: { options: [
    { label: 'Toggle Heatmap', command: 'toggle heatmap' },
    { label: 'Toggle Markers', command: 'toggle markers' },
    { label: 'Toggle Clusters', command: 'toggle clusters' },
    { label: 'Toggle Zones', command: 'toggle zones' },
    { label: 'Toggle Labels', command: 'toggle marker labels' },
  ]},
  mapTheme: { options: [
    { label: 'Agricultural', command: 'use agricultural theme' },
    { label: 'Thermal', command: 'use thermal theme' },
    { label: 'Ocean', command: 'use ocean theme' },
    { label: 'Grayscale', command: 'use grayscale theme' },
  ]},
  mapDisplay: { options: [
    { label: 'Toggle Legend', command: 'toggle legend' },
    { label: 'Toggle Tooltips', command: 'toggle tooltips' },
    { label: 'Toggle Controls', command: 'toggle controls' },
    { label: 'Show Scale Bar', command: 'show scale bar' },
  ]},
  mapData: { options: [
    { label: 'Refresh Data', command: 'refresh data' },
    { label: 'Enable Real-time', command: 'enable real-time' },
    { label: 'Pause Updates', command: 'pause updates' },
    { label: 'Sample Data', command: 'generate sample data' },
  ]},
  dialTheme: { options: [
    { label: 'Neon Glow', command: 'set theme neon-glow' },
    { label: 'Gradient Arc', command: 'set theme gradient-arc' },
    { label: 'Minimal', command: 'set theme minimal' },
    { label: 'Dot Ring', command: 'set theme dot-ring' },
    { label: 'Dot Ring Static', command: 'set theme dot-ring-static' },
    { label: 'Ambient', command: 'set theme ambient' },
  ]},
  toggleTheme: { options: [
    { label: 'iOS', command: 'set theme to ios' },
    { label: 'Material', command: 'set theme to material' },
    { label: 'Neon', command: 'set theme to neon' },
    { label: 'Minimal', command: 'set theme to minimal' },
    { label: 'Pill', command: 'set theme to pill' },
  ]},
  statusLightThresholds: { options: [
    { label: 'Percentage (70/90)', command: 'set warning threshold to 70 and critical threshold to 90' },
    { label: 'Temperature (60/80)', command: 'set warning threshold to 60 and critical threshold to 80' },
    { label: 'Low-is-bad (30/10)', command: 'set warning threshold to 30 and critical threshold to 10 and threshold direction below' },
    { label: 'Clear Thresholds', command: 'clear thresholds' },
  ]},
  statusLightShape: { options: [
    { label: 'Circle', command: 'set shape to circle' },
    { label: 'Square', command: 'set shape to square' },
    { label: 'Diamond', command: 'set shape to diamond' },
  ]},
  statusLightBlink: { options: [
    { label: 'None', command: 'stop blinking' },
    { label: 'Slow Blink', command: 'blink slow' },
    { label: 'Fast Blink', command: 'blink fast' },
    { label: 'Pulse', command: 'pulse' },
    { label: 'Blink on Critical', command: 'blink on critical' },
  ]},
  statusLightTheme: { options: [
    { label: 'LED', command: 'set theme to led' },
    { label: 'Flat', command: 'set theme to flat' },
    { label: 'Glow', command: 'set theme to glow' },
    { label: 'Minimal', command: 'set theme to minimal' },
    { label: 'Traffic Light', command: 'set theme to traffic-light' },
  ]},
  pushButtonTheme: { options: [
    { label: 'Power', command: 'set theme to power' },
    { label: 'Circular', command: 'set theme to circular' },
    { label: 'Pill', command: 'set theme to pill' },
    { label: 'Material', command: 'set theme to material' },
    { label: 'Neon', command: 'set theme to neon' },
  ]},
  pushButtonShape: { options: [
    { label: 'Circle', command: 'set shape to circle' },
    { label: 'Rounded Rect', command: 'set shape to rounded-rect' },
    { label: 'Pill', command: 'set shape to pill' },
  ]},
  iconButtonTheme: { options: [
    { label: 'Circular', command: 'set theme to circular' },
    { label: 'Rounded Rect', command: 'set theme to rounded-rect' },
    { label: 'Pill', command: 'set theme to pill' },
    { label: 'Ghost', command: 'set theme to ghost' },
    { label: 'Neon', command: 'set theme to neon' },
    { label: 'Material', command: 'set theme to material' },
  ]},
  iconButtonShape: { options: [
    { label: 'Circle', command: 'set shape to circular' },
    { label: 'Rounded Rect', command: 'set shape to rounded-rect' },
    { label: 'Pill', command: 'set shape to pill' },
    { label: 'Square', command: 'set shape to square' },
  ]},
  stepperTheme: { options: [
    { label: 'Flat', command: 'set theme to flat' },
    { label: 'Rounded', command: 'set theme to rounded' },
    { label: 'Pill', command: 'set theme to pill' },
    { label: 'Material', command: 'set theme to material' },
    { label: 'Neon', command: 'set theme to neon' },
  ]},
  sliderTheme: { options: [
    { label: 'Flat', command: 'set theme to flat' },
    { label: 'Gradient', command: 'set theme to gradient' },
    { label: 'Neon Track', command: 'set theme to neon-track' },
    { label: 'Minimal', command: 'set theme to minimal' },
    { label: 'Stepped', command: 'set theme to stepped' },
  ]},
  sliderTicks: { options: [
    { label: 'Inside Ticks', command: 'inside ticks' },
    { label: 'Outside Ticks', command: 'outside ticks' },
    { label: 'No Ticks', command: 'no ticks' },
  ]},
  levelTheme: { options: [
    { label: 'Battery', command: 'set theme to battery' },
    { label: 'Signal', command: 'set theme to signal' },
    { label: 'LED', command: 'set theme to led' },
    { label: 'Gradient', command: 'set theme to gradient' },
    { label: 'Minimal', command: 'set theme to minimal' },
  ]},
  segmentedTheme: { options: [
    { label: 'Default', command: 'set theme to default' },
    { label: 'Pill', command: 'set theme to pill' },
    { label: 'Underline', command: 'set theme to underline' },
    { label: 'Bordered', command: 'set theme to bordered' },
  ]},
  levelOrientation: { options: [
    { label: 'Vertical', command: 'set orientation to vertical' },
    { label: 'Horizontal', command: 'set orientation to horizontal' },
  ]},
  thermostatUnits: { options: [
    { label: '°F', command: 'set unit to fahrenheit' },
    { label: '°C', command: 'set unit to celsius' },
  ]},
  headingLevel: { options: [
    { label: 'H1', command: 'set heading level to h1' },
    { label: 'H2', command: 'set heading level to h2' },
    { label: 'H3', command: 'set heading level to h3' },
    { label: 'Body', command: 'set heading level to body' },
  ]},
};

/** Color property options per component type — used for the color picker sub-menu */
export interface ColorPropertyOption {
  label: string;
  commandPrefix: string;
}

const chartColorPropertyOptions: ColorPropertyOption[] = [
  { label: 'Series Colors', commandPrefix: 'set chart colors to ' },
  { label: 'Background', commandPrefix: 'set chart background to ' },
  { label: 'Grid Color', commandPrefix: 'set grid color to ' },
];

export const colorPropertyDrilldowns: Record<string, ColorPropertyOption[]> = {
  [ComponentType.TEXT]: [
    { label: 'Text Color', commandPrefix: 'set color to ' },
    { label: 'Background', commandPrefix: 'set background to ' },
  ],
  [ComponentType.DATA_TEXT]: [
    { label: 'Value Color', commandPrefix: 'set value color to ' },
    { label: 'Label Color', commandPrefix: 'set label color to ' },
    { label: 'Unit Color', commandPrefix: 'set unit color to ' },
  ],
  [ComponentType.CHART]: chartColorPropertyOptions,
  [ComponentType.BAR_CHART]: chartColorPropertyOptions,
  [ComponentType.LINE_CHART]: chartColorPropertyOptions,
  [ComponentType.PIE_CHART]: chartColorPropertyOptions,
  [ComponentType.SCATTER_CHART]: chartColorPropertyOptions,
  [ComponentType.AREA_CHART]: chartColorPropertyOptions,
  [ComponentType.HEATMAP_CHART]: chartColorPropertyOptions,
  [ComponentType.TREEMAP_CHART]: chartColorPropertyOptions,
  [ComponentType.BUTTON]: [
    { label: 'Text Color', commandPrefix: 'set text color to ' },
    { label: 'Background', commandPrefix: 'set background color to ' },
    { label: 'Hover Background', commandPrefix: 'set hover background to ' },
  ],
  [ComponentType.INPUT]: [
    { label: 'Text Color', commandPrefix: 'set text color to ' },
    { label: 'Background', commandPrefix: 'set background color to ' },
    { label: 'Border Color', commandPrefix: 'set border color to ' },
  ],
  [ComponentType.RADIAL_DIAL]: [
    { label: 'Fill Color', commandPrefix: 'set fill color to ' },
    { label: 'Track Color', commandPrefix: 'set track color to ' },
    { label: 'Glow Color', commandPrefix: 'set glow color to ' },
    { label: 'Knob Color', commandPrefix: 'set knob color to ' },
  ],
  [ComponentType.ICON_BUTTON]: [
    { label: 'Active Color', commandPrefix: 'set active color to ' },
    { label: 'Inactive Color', commandPrefix: 'set inactive color to ' },
    { label: 'Active Icon', commandPrefix: 'set active icon color to ' },
    { label: 'Inactive Icon', commandPrefix: 'set inactive icon color to ' },
    { label: 'Border Color', commandPrefix: 'set border color to ' },
    { label: 'Hover Color', commandPrefix: 'set hover color to ' },
  ],
  [ComponentType.PUSH_BUTTON]: [
    { label: 'Button Color', commandPrefix: 'set release color to ' },
    { label: 'Press Color', commandPrefix: 'set press color to ' },
    { label: 'Glow Color', commandPrefix: 'set glow color to ' },
    { label: 'Border Color', commandPrefix: 'set border color to ' },
    { label: 'Text Color', commandPrefix: 'set text color to ' },
  ],
  [ComponentType.TOGGLE_SWITCH]: [
    { label: 'On Color', commandPrefix: 'set on color to ' },
    { label: 'Off Color', commandPrefix: 'set off color to ' },
    { label: 'Thumb Color', commandPrefix: 'set thumb color to ' },
    { label: 'Thumb Border', commandPrefix: 'set border color to ' },
    { label: 'Track Border', commandPrefix: 'set track border color to ' },
  ],
  [ComponentType.STATUS_LIGHT]: [
    { label: 'OK Color', commandPrefix: 'set ok color to ' },
    { label: 'Warning Color', commandPrefix: 'set warning color to ' },
    { label: 'Critical Color', commandPrefix: 'set critical color to ' },
    { label: 'Offline Color', commandPrefix: 'set offline color to ' },
  ],
  [ComponentType.ICON]: [
    { label: 'Icon Color', commandPrefix: 'set color to ' },
  ],
  [ComponentType.BREADCRUMB]: [
    { label: 'Color', commandPrefix: 'set color to ' },
  ],
  [ComponentType.CONTAINER]: [
    { label: 'Background', commandPrefix: 'set background to ' },
    { label: 'Border Color', commandPrefix: 'set border color to ' },
  ],
  [ComponentType.CARD]: [
    { label: 'Background', commandPrefix: 'set card background color to ' },
    { label: 'Border Color', commandPrefix: 'set card border color to ' },
    { label: 'Icon Color', commandPrefix: 'set icon color to ' },
  ],
  [ComponentType.SLIDER]: [
    { label: 'Track Color', commandPrefix: 'set track color to ' },
    { label: 'Thumb Color', commandPrefix: 'set thumb color to ' },
  ],
  [ComponentType.LEVEL_INDICATOR]: [
    { label: 'Fill Color', commandPrefix: 'set fill color to ' },
    { label: 'Track Color', commandPrefix: 'set track color to ' },
  ],
};
